import { Grid, Typography, IconButton } from "@material-ui/core";
import {
    makeStyles,
    createStyles,
    Theme,
    useTheme,
} from "@material-ui/core/styles";
import LinkIcon from "@material-ui/icons/Link";
import {
    FacebookShareButton,
    TwitterShareButton,
    WhatsappShareButton,
    LinkedinShareButton,
    TelegramShareButton,
    EmailShareButton,
    FacebookIcon,
    TwitterIcon,
    WhatsappIcon,
    LinkedinIcon,
    TelegramIcon,
    EmailIcon,
} from "react-share";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";

type ShareOptions = {
    type:
        | "facebook"
        | "twitter"
        | "whatsapp"
        | "linkedin"
        | "telegram"
        | "email"
        | "link";
    url: string;
    data: Compleo.IObject;
};

interface IProps {
    options: ShareOptions[];
    language: string;
    t: any;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        sticky: {
            position: "sticky",
            top: "9rem",
        },
        subtitle: {
            fontFamily: theme.typography.subtitle1.fontFamily,
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: theme.typography.subtitle1.fontWeight,
            lineHeight: theme.typography.subtitle1.lineHeight,
        },
    })
);

const Share = (props: IProps) => {
    const { language, t } = props;

    const classes = useStyles();
    const showMessage = useShowMessage();

    const copyTextToClipboard = async (text: string) => {
        if ("clipboard" in navigator) {
            return await navigator.clipboard.writeText(text);
        } else {
            return document.execCommand("copy", true, text);
        }
    };

    const handleLinkCopy = async (
        event: React.MouseEvent<HTMLButtonElement>,
        text: string
    ) => {
        event.preventDefault();
        await copyTextToClipboard(text);
        const copyLinkMessageText = !t(
            "jobDetail_jobShareCopyLinkText"
        ).includes("jobDetail_jobShareCopyLinkText")
            ? t("jobDetail_jobShareCopyLinkText")
            : language !== "pt-BR"
            ? "The job link has been copied to your clipboard!"
            : "Link copiado com sucesso!";
        showMessage(copyLinkMessageText, "success");
    };

    return (
        <Grid container direction="column" spacing={1}>
            <Grid item>
                <Typography variant="subtitle1" className={classes.subtitle}>
                    {!t("jobDetail_jobShareText").includes(
                        "jobDetail_jobShareText"
                    )
                        ? t("jobDetail_jobShareText")
                        : language !== "pt-BR"
                        ? "Share with your friends"
                        : "Compartilhe essa vaga"}
                </Typography>
            </Grid>
            <Grid item>
                <Grid
                    container
                    direction="row"
                    justify="flex-start"
                    alignItems="center"
                    spacing={2}
                >
                    {props.options.map((option, index) => (
                        <Grid item key={index}>
                            {option.type === "facebook" && (
                                <FacebookShareButton
                                    url={option.url}
                                    quote={option.data.quote}
                                >
                                    <FacebookIcon size={32} round />
                                </FacebookShareButton>
                            )}
                            {option.type === "twitter" && (
                                <TwitterShareButton
                                    url={option.url}
                                    title={option.data.title}
                                    hashtags={
                                        option.data.hashtags
                                            ? option.data.hashtags
                                            : undefined
                                    }
                                >
                                    <TwitterIcon size={32} round />
                                </TwitterShareButton>
                            )}
                            {option.type === "whatsapp" && (
                                <WhatsappShareButton
                                    url={option.url}
                                    title={option.data.title}
                                >
                                    <WhatsappIcon size={32} round />
                                </WhatsappShareButton>
                            )}
                            {option.type === "linkedin" && (
                                <LinkedinShareButton
                                    url={option.url}
                                    title={option.data.title}
                                    summary={option.data.summary}
                                    source={
                                        option.data.source
                                            ? option.data.source
                                            : undefined
                                    }
                                >
                                    <LinkedinIcon size={32} round />
                                </LinkedinShareButton>
                            )}
                            {option.type === "telegram" && (
                                <TelegramShareButton
                                    url={option.url}
                                    title={option.data.title}
                                >
                                    <TelegramIcon size={32} round />
                                </TelegramShareButton>
                            )}
                            {option.type === "email" && (
                                <EmailShareButton
                                    url={option.url}
                                    subject={option.data.subject}
                                    body={option.data.body}
                                    openShareDialogOnClick={true}
                                >
                                    <EmailIcon size={32} round />
                                </EmailShareButton>
                            )}
                            {option.type === "link" && (
                                <IconButton
                                    size="small"
                                    onClick={(e) =>
                                        handleLinkCopy(e, option.url)
                                    }
                                >
                                    <LinkIcon style={{ fontSize: 32 }} />
                                </IconButton>
                            )}
                        </Grid>
                    ))}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default Share;
